import React from 'react'

export type AverageWeightGrowthPoint = {
  date: string
  weightG: number
  lot: string
}

type Props = {
  points: AverageWeightGrowthPoint[]
  height?: number
  width?: number
  showLegend?: boolean
  maxLotsInLegend?: number
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316']

export const AverageWeightGrowthChart: React.FC<Props> = ({
  points,
  height = 240,
  width = 1000,
  showLegend = true,
  maxLotsInLegend = 8
}) => {
  const chartData = points
    .filter((point) => point.weightG > 0)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((point) => ({
      ...point,
      displayDate: new Date(point.date).toLocaleDateString()
    }))

  if (chartData.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
        No weight data available for chart
      </div>
    )
  }

  const isCompact = width <= 820
  const padding = {
    top: 50,
    right: showLegend ? (isCompact ? 110 : 140) : 40,
    bottom: isCompact ? 60 : 55,
    left: isCompact ? 48 : 60
  }

  const lotData = chartData.reduce((groups, point) => {
    if (!groups[point.lot]) groups[point.lot] = []
    groups[point.lot].push(point)
    return groups
  }, {} as Record<string, typeof chartData>)

  const lotNames = Object.keys(lotData)
  const lotColors: Record<string, string> = {}
  lotNames.forEach((lot, index) => {
    lotColors[lot] = DEFAULT_COLORS[index % DEFAULT_COLORS.length]
  })

  const minWeight = Math.min(...chartData.map((point) => point.weightG))
  const maxWeight = Math.max(...chartData.map((point) => point.weightG))
  const weightRange = maxWeight - minWeight || 1

  const minDate = new Date(Math.min(...chartData.map((point) => new Date(point.date).getTime())))
  const maxDate = new Date(Math.max(...chartData.map((point) => new Date(point.date).getTime())))
  const dateRange = maxDate.getTime() - minDate.getTime() || 1

  const lotPoints = lotNames.map((lot) => {
    const points = lotData[lot].map((point) => {
      const x = padding.left + ((new Date(point.date).getTime() - minDate.getTime()) / dateRange) * (width - padding.left - padding.right)
      const y = height - padding.bottom - ((point.weightG - minWeight) / weightRange) * (height - padding.top - padding.bottom)
      return { x, y, ...point }
    })
    return { lot, points, color: lotColors[lot] }
  })

  const xAxisPoints = lotPoints.reduce((best, candidate) => (
    candidate.points.length > best.points.length ? candidate : best
  ), lotPoints[0])

  const legendLimit = isCompact ? Math.min(maxLotsInLegend, 4) : maxLotsInLegend
  const legendLots = showLegend ? lotNames.slice(0, Math.max(0, legendLimit)) : []
  const yTickRatios = isCompact ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]
  const xAxisTargetCount = isCompact ? 4 : 8
  const xAxisStride = Math.max(1, Math.floor(xAxisPoints.points.length / xAxisTargetCount))

  return (
    <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <svg
        width={width}
        height={height}
        style={{ border: '1px solid #e5e7eb', borderRadius: '8px', position: 'relative' }}
      >
        {/* Grid lines + Y-axis labels */}
        {yTickRatios.map((ratio) => (
          <g key={ratio}>
            <line
              x1={padding.left}
              y1={height - padding.bottom - ratio * (height - padding.top - padding.bottom)}
              x2={width - padding.right}
              y2={height - padding.bottom - ratio * (height - padding.top - padding.bottom)}
              stroke="#f3f4f6"
              strokeWidth={1}
            />
            <text
              x={padding.left - 10}
              y={height - padding.bottom - ratio * (height - padding.top - padding.bottom) + 4}
              textAnchor="end"
              fontSize={isCompact ? '11' : '12'}
              fill="#6b7280"
            >
              {(minWeight + ratio * weightRange).toFixed(0)}g
            </text>
          </g>
        ))}

        {/* X-axis labels (sampled from first lot) */}
        {lotPoints.length > 0
          ? xAxisPoints.points
              .filter((_, i) => i % xAxisStride === 0)
              .map((point) => (
                <text
                  key={`x-axis-${point.date}`}
                  x={point.x}
                  y={height - padding.bottom + 20}
                  textAnchor="middle"
                  fontSize={isCompact ? '10' : '11'}
                  fill="#6b7280"
                  transform={`rotate(-45 ${point.x} ${height - padding.bottom + 20})`}
                >
                  {point.displayDate}
                </text>
              ))
          : null}

        {/* Lines + points */}
        {lotPoints.map(({ lot, points, color }) => {
          const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
          return (
            <g key={lot}>
              <path
                d={pathData}
                stroke={color}
                strokeWidth={3}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {points.map((point) => (
                <g key={`${point.date}-${point.lot}`}>
                  <circle cx={point.x} cy={point.y} r={5} fill={color} stroke="white" strokeWidth={2} />
                  <circle cx={point.x} cy={point.y} r={12} fill="transparent" style={{ cursor: 'pointer' }}>
                    <title>{`${point.lot}\nDate: ${point.displayDate}\nWeight: ${point.weightG.toFixed(1)}g`}</title>
                  </circle>
                </g>
              ))}
            </g>
          )
        })}

        {/* Legend */}
        {showLegend
          ? legendLots.map((lot, i) => (
              <g key={lot}>
                <rect
                  x={width - padding.right + 30}
                  y={padding.top + i * 20}
                  width={12}
                  height={12}
                  fill={lotColors[lot]}
                  rx={2}
                />
                <text x={width - padding.right + 48} y={padding.top + i * 20 + 9} fontSize="11" fill="#374151">
                  {lot}
                </text>
              </g>
            ))
          : null}

        {/* Axis labels */}
        <text
          x={15}
          y={height / 2}
          textAnchor="middle"
          fontSize="12"
          fill="#6b7280"
          transform={`rotate(-90, 15, ${height / 2})`}
        >
          Weight (grams)
        </text>
        <text x={width / 2} y={height - 10} textAnchor="middle" fontSize="12" fill="#6b7280">
          Date
        </text>
      </svg>
    </div>
  )
}
